/**
 * SessionStatusChip
 *
 * Persistent badge shown on every Teaching Facilitation phase after the start
 * screen. Displays the session code and sync state — click to copy the code.
 *
 * This is the teacher's "where's my work" signal and their anchor for coming
 * back to a session later. It must always be visible and always truthful.
 */

import { useState } from 'react';
import { Check, Copy, Cloud, CloudOff, Loader2 } from 'lucide-react';
import type { SyncState } from '../../hooks/useTeachingSession';

interface Props {
  code: string;
  syncState: SyncState;
  language: 'en' | 'es';
}

export function SessionStatusChip({ code, syncState, language }: Props) {
  const [copied, setCopied] = useState(false);

  const t = (en: string, es: string) => (language === 'en' ? en : es);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard denied (rare) — fall back to nothing; user can still read it.
    }
  };

  const label =
    syncState === 'saving'
      ? t('Saving…', 'Guardando…')
      : syncState === 'offline'
        ? t('Offline — will retry', 'Sin conexión — reintentando')
        : syncState === 'loading'
          ? t('Loading…', 'Cargando…')
          : t('Saved', 'Guardado');

  const Icon =
    syncState === 'saving'
      ? Loader2
      : syncState === 'offline'
        ? CloudOff
        : syncState === 'loading'
          ? Loader2
          : Cloud;

  const tint =
    syncState === 'offline'
      ? { fg: '#92400e', bg: '#fef3c7', ring: '#f59e0b' }
      : syncState === 'saving' || syncState === 'loading'
        ? { fg: '#1f2937', bg: '#e5e7eb', ring: '#9ca3af' }
        : { fg: '#2a6d5b', bg: '#e6f4ec', ring: '#42a57a' };

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={t('Copy session code', 'Copiar código de sesión')}
      className="inline-flex items-center gap-2 text-xs md:text-sm rounded-full px-3 py-2 font-semibold shadow-sm hover:shadow transition-all"
      style={{
        background: tint.bg,
        color: tint.fg,
        border: `1.5px solid ${tint.ring}`,
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      <Icon
        className={`w-4 h-4 ${syncState === 'saving' || syncState === 'loading' ? 'animate-spin' : ''}`}
        strokeWidth={2}
      />
      <span className="hidden sm:inline">{label} ·</span>
      <span className="font-mono tracking-wide">{code}</span>
      {copied ? (
        <Check className="w-4 h-4" strokeWidth={2.5} />
      ) : (
        <Copy className="w-3.5 h-3.5 opacity-70" strokeWidth={2} />
      )}
    </button>
  );
}
